/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

// Name of the file the recovery codes download as.
const RECOVERY_CODES_FILENAME = 'recovery-codes.txt';

/**
 * קודי השחזור - הסבר תחילה, והקודים עצמם רק למי שמבקש לראות אותם.
 *
 * ## למה הם אינם על המסך
 *
 * עשרה סודות שנפרשים על מסך ההרשמה תופסים את רוב הגובה שלו, ומי שרואה אותם בפעם
 * הראשונה אינו יודע מהם - הוא רואה רשימת מחרוזות ולוחץ "הבא". במה בגודל הזה בלי
 * הסבר אינה מלמדת דבר, והיא דוחקת את מה שהלקוח בא לעשות אל מחוץ למסך.
 *
 * לכן הסדר הפוך: קודם מה הם ומתי משתמשים בהם, אחר כך שמירה בלחיצה אחת - הורדה או
 * העתקה, שתיהן שומרות את כל העשרה בלי לקרוא אפילו אחד מהם - ורק בסוף, למי שרוצה,
 * הצגה על המסך.
 *
 * **הסתרה אינה אובדן.** הקודים נשמרים כתקציר ואינם ניתנים להצגה חוזרת, אבל אפשר
 * להנפיק מנה חדשה בכל רגע מהגדרות האבטחה שבאזור האישי - וזה בדיוק מה שההסבר אומר,
 * כדי שהלקוח לא ירגיש שהוא עומד לאבד משהו בלתי הפיך.
 */
export default function RecoveryCodes({codes}) {
    const {t} = useTranslation();
    const [revealed, setRevealed] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    /**
     * הקודים כטקסט, מאחורי שתי שורות שאומרות מה הם.
     *
     * קובץ או הדבקה שיתגלו בעוד שנה צריכים להסביר את עצמם: רשימת מחרוזות בלי כותרת
     * היא בדיוק מה שנמחק בניקיון התיקיות.
     */
    const asText = () => [
        t("Two-factor recovery codes"),
        t("Each code can be used once"),
        "",
        ...codes,
    ].join("\n");

    /**
     * העתקה במסך בלי `navigator.clipboard`, שהדפדפן מונע מחוץ להקשר מאובטח. זורק
     * כשההעתקה נדחית, כדי ששני המסלולים ידווחו על כישלון באותה דרך.
     */
    const copyThroughHiddenField = (text) => {
        const field = document.createElement("textarea");
        field.value = text;
        field.setAttribute("readonly", "");
        // מחוץ למסך אך עדיין ניתן לסימון; מ-`display: none` אי אפשר להעתיק.
        field.style.position = "fixed";
        field.style.top = "-1000px";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        try {
            if (!document.execCommand("copy")) {
                throw new Error("the browser refused the copy");
            }
        } finally {
            document.body.removeChild(field);
        }
    };

    const handleCopy = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(asText());
            } else {
                copyThroughHiddenField(asText());
            }
            setError(null);
            setMessage("Recovery codes copied");
        } catch (e) {
            setMessage(null);
            setError("The codes could not be copied. Please write them down instead");
        }
    };

    const handleDownload = () => {
        const url = URL.createObjectURL(
            new Blob([asText()], {type: "text/plain;charset=utf-8"})
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = RECOVERY_CODES_FILENAME;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // ה-blob מוחזק עד שהוא משוחרר, וההורדה כבר נמסרה לדפדפן.
        URL.revokeObjectURL(url);
        setError(null);
        setMessage("Recovery codes downloaded");
    };

    if (codes.length === 0) {
        return null;
    }

    return (
        <div className='recovery-notice'>
            <h2 className='teal'>{t("Recovery codes - your way in when a code cannot reach you")}</h2>
            <h2>{t("If you lose the phone, change the number or cannot open the mailbox, a recovery code signs you in instead. Each of the codes below works once.")}</h2>
            <h2>{t("Save them somewhere you will find without your phone. You can also continue now and create fresh codes at any time from the security settings in your account.")}</h2>

            {/*
                שמירה - **פעולה משנית**, ולא הכפתור הראשי של המסך.

                `button-outline` ולא `button-dark-blue`: הכפתור הראשי כאן הוא "הבא"
                שמתחת לכרטיס, ואפשר להמשיך בלי לשמור דבר - הקודים ניתנים להנפקה
                מחדש בכל עת מהגדרות האבטחה. שני כפתורים מלאים בתוך הכרטיס הפכו את
                השמירה לשלב שנראה חובה, ואת ההמשך למה שמתחבא מתחתיו.
            */}
            <div className='button-row'>
                <div className='button button-center button-outline' onClick={handleDownload}>{t("Download codes")}</div>
                <div className='button button-center button-outline' onClick={handleCopy}>{t("Copy codes")}</div>
            </div>

            {/*
                ההצגה על המסך אחרונה, וסגורה כברירת מחדל.

                שמירה אינה דורשת קריאה - הורדה והעתקה לוקחות את כל העשרה - ומי שפותח
                את הרשימה עושה זאת כדי להעתיק אותה ביד או כדי לראות במה מדובר.
            */}
            <h2 className='mt20 teal pointer' onClick={() => setRevealed(!revealed)}>
                {revealed ? t("Hide the codes") : t("Show the codes on screen")}
            </h2>

            {revealed && (
                <div className='recovery-codes'>
                    {codes.map((code) => (
                        <div key={code} className='recovery-code'>{code}</div>
                    ))}
                </div>
            )}

            {message && <h2 className='mt20 teal'>{t(message)}</h2>}
            {error && <h2 className='mt20 error'>{t(error)}</h2>}
        </div>
    );
}
